(() => {
  const enhanced = new WeakMap();
  const normalize = text => String(text || '').replace(/\s+/g, ' ').trim();
  const token = text => normalize(text).toUpperCase().replace(/[^A-Z0-9]/g, '');
  const sizeToken = text => ({ SMALL: 'S', MEDIUM: 'M', LARGE: 'L', XLARGE: 'XL', XXL: '2XL', XXXL: '3XL', OS: 'ONE', ONESIZE: 'ONE', QTY: 'ONE' }[token(text)] || token(text));
  const stockSizes = new Set(['XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL']);
  const colors = { BLK: ['BLACK'], IND: ['INDIGO'], NVY: ['NAVY'], ECRU: ['ECRU'], OAT: ['OATMEAL'], WHT: ['WHITE'], NAT: ['NATURAL'], GRY: ['GREY', 'GRAY'], BRN: ['BROWN'], OLV: ['OLIVE'], BLU: ['BLUE'], RED: ['RED'] };
  const origin = text => /^(?:made in\s|fabriqu[ée]e? (?:au|en)\s)/iu.test(normalize(text));
  const skuWord = text => /(^|[^\p{L}\p{N}_])sku(?=$|[^\p{L}\p{N}_])/iu.test(text);
  const removeSkuFields = (description, root) => {
    description.querySelectorAll('p, div').forEach(element => {
      if (element.closest('li') || element.querySelector('p, div, ul, ol, table')) return;
      const text = element.textContent;
      const fields = [...text.matchAll(/(^|[^\p{L}\p{N}_])sku\s*[:#=]\s*[a-z0-9][a-z0-9._/-]*(?:[ \t]*(?:[;|–—][ \t]*)?)/giu)]
        .map(match => [match.index + match[1].length, match.index + match[0].length]);
      const standalone = text.match(/^\s*sku\s+([a-z0-9][a-z0-9._/-]*)\s*$/iu);
      if (!fields.length && standalone && token(standalone[1]) === token(root)) fields.push([0, text.length]);
      for (const [start, end] of fields.reverse()) {
        const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
        const nodes = [];
        while (walker.nextNode()) nodes.push(walker.currentNode);
        const point = offset => {
          let count = 0;
          for (const node of nodes) {
            if (offset <= count + node.length) return [node, offset - count];
            count += node.length;
          }
          return [nodes.at(-1), nodes.at(-1)?.length || 0];
        };
        const range = document.createRange();
        range.setStart(...point(start)); range.setEnd(...point(end)); range.deleteContents();
      }
      if (fields.length && !normalize(element.textContent) && !element.querySelector('img, video, iframe, input, select, button')) element.remove();
    });
  };
  const resolveSku = data => {
    const variants = data.variants || [];
    if (!variants.length || variants.every(variant => !normalize(variant.sku))) return { status: 'missing' };
    if ((data.variantCount || 0) > variants.length || variants.some(variant => !normalize(variant.sku))) return { status: 'ambiguous' };
    const options = variant => variant.selectedOptions || (variant.options || []).map((value, index) => ({ name: data.options?.[index], value }));
    const size = variant => options(variant).find(option => /^(size|taille)$/i.test(option.name || ''))?.value;
    const tails = variants.map(variant => sizeToken(variant.sku.split('-').at(-1)));
    const warehouseSizes = variants.length > 1 && variants.every(variant => size(variant)) && tails.every(tail => stockSizes.has(tail)) && new Set(tails).size === new Set(variants.map(variant => size(variant))).size;
    const roots = variants.map(variant => {
      const parts = normalize(variant.sku).split('-');
      const last = parts.at(-1);
      const selectedSize = size(variant);
      const selectedColor = options(variant).find(option => /^(colou?r|couleur)$/i.test(option.name || ''))?.value;
      const sizeMatched = selectedSize && (sizeToken(last) === sizeToken(selectedSize) || warehouseSizes);
      if (parts.length > 1 && sizeMatched) parts.pop();
      const color = parts.at(-1);
      const names = colors[token(color)];
      const colorValues = [selectedColor, ...(Array.isArray(data.color) ? data.color : [data.color])].filter(Boolean).map(token);
      const titleWords = normalize(data.title).toUpperCase().split(/[^A-Z0-9]+/);
      const nfdStockColor = data.vendor === 'Naked & Famous Denim' && parts.length === 2 && /^(?:\d{9}|SMU\d+)$/i.test(parts[0]) && Boolean(names);
      const colorMatched = nfdStockColor || colorValues.includes(token(color)) || names?.some(name => colorValues.includes(name) || titleWords.includes(name));
      if (parts.length > 1 && colorMatched) parts.pop();
      else if (parts.length > 1 && names) return null;
      if (parts.length > 1 && !sizeMatched && !colorMatched && variants.length === 1) return null;
      if (parts.length > 1 && !sizeMatched && !colorMatched && new Set(variants.map(item => item.sku)).size !== 1) return null;
      return parts.join('-');
    });
    return roots.every(root => root && root === roots[0]) ? { status: 'resolved', root: roots[0] } : { status: 'ambiguous' };
  };
  const initialize = () => {
    document.querySelectorAll('template[data-nfd-studio-layout]').forEach(marker => {
      const product = [...document.querySelectorAll('nfd-womens-product')].find(element => element.dataset.productId === marker.dataset.nfdStudioLayout);
      if (!product || enhanced.has(product)) return;
      const count = Number(marker.dataset.visualCount);
      const inline = marker.dataset.inlineDetails === 'true';
      product.toggleAttribute('data-nfd-few-visuals', count < 3);
      product.toggleAttribute('data-nfd-no-visual', count === 0);
      product.toggleAttribute('data-nfd-single-visual', count === 1);
      product.toggleAttribute('data-nfd-inline-details', inline);
      const desktop = matchMedia('(min-width: 1000px)');
      const source = marker.content.querySelector('[data-nfd-description-source]');
      let data;
      try { data = JSON.parse(marker.content.querySelector('[data-nfd-details-product]')?.textContent || 'null'); } catch {}
      const sku = data ? resolveSku(data) : { status: 'missing' };
      product.dataset.nfdSkuStatus = sku.status;
      const prepared = new WeakMap();
      let frame;
      let specifications = document.querySelector('[data-women-editorial-product="' + product.dataset.productId + '"] .nfd-women-editorial__specifications');
      const outerRows = description => [...description.querySelectorAll('li')].filter(item => !item.parentElement.closest('li'));
      const prepareDescription = description => {
        if (prepared.has(description)) return prepared.get(description);
        if (source) description.replaceChildren(...source.cloneNode(true).childNodes);
        const fitText = normalize(product.querySelector('.nfd-women-fit p')?.textContent);
        if (fitText) description.querySelectorAll('p, div').forEach(element => {
          if (!element.querySelector('p, div, ul, ol') && normalize(element.textContent) === fitText) element.remove();
        });
        if (data) {
          if (sku.root) {
            description.querySelectorAll('li').forEach(item => { if (skuWord(item.textContent)) item.remove(); });
            removeSkuFields(description, sku.root);
          }
          const text = description.textContent.toLowerCase();
          const value = field => Array.isArray(field) ? field[0] : field;
          const extras = [];
          if (value(data.closure) && !/ fly|braguette|fermeture/.test(text)) extras.push(value(data.closure));
          if (value(data.hardware) && !/hardware|pièces métalliques|détails métalliques|quincaillerie/.test(text)) extras.push(data.hardwareLabel);
          const countries = outerRows(description).filter(item => origin(item.textContent));
          if (extras.length || sku.root || countries.length) {
            const lists = [...description.querySelectorAll('ul, ol')].filter(list => !list.closest('li'));
            const list = lists.at(-1) || description.appendChild(document.createElement('ul'));
            extras.forEach(text => { const item = document.createElement('li'); item.textContent = text; list.append(item); });
            if (sku.root) { const item = document.createElement('li'); item.dataset.nfdRootSku = ''; item.textContent = 'SKU: ' + sku.root; list.append(item); }
            countries.forEach(item => list.append(item));
          }
        }
        const lead = normalize(product.querySelector('[data-women-intro-text]')?.textContent);
        const opening = lead ? [...description.querySelectorAll('p, div')].find(element => !element.querySelector('p, div, ul, ol') && normalize(element.textContent).startsWith(lead)) : null;
        const state = { opening, original: opening?.cloneNode(true), fullLead: normalize(opening?.textContent) };
        prepared.set(description, state);
        const rows = outerRows(description);
        if (!specifications && rows.length && product.querySelector('.nfd-women-information')) {
          specifications = document.createElement('div');
          specifications.className = 'nfd-women-editorial__specifications';
          const heading = document.createElement('h2');
          heading.textContent = marker.dataset.detailsLabel || 'Details';
          specifications.append(heading);
          product.querySelector('.nfd-women-information').prepend(specifications);
        }
        if (specifications) {
          const list = document.createElement('ul');
          rows.forEach(item => list.append(item.cloneNode(true)));
          specifications.querySelector('ul')?.remove();
          if (list.children.length) specifications.append(list);
          specifications.hidden = !list.children.length;
        }
        return state;
      };
      const meaningful = panel => {
        const copy = panel.cloneNode(true);
        copy.querySelectorAll('[hidden], [data-women-empty-block], .nfd-women-panel-heading').forEach(element => element.remove());
        if (desktop.matches && specifications && !specifications.hidden && panel.querySelector('[data-women-description]')) copy.querySelectorAll('[data-women-description] ul, [data-women-description] ol').forEach(list => list.remove());
        return normalize(copy.textContent) || copy.querySelector('img, video, iframe, table, input, select, button, a');
      };
      const sync = () => {
        frame = null;
        const fit = product.querySelector('.nfd-women-information__intro');
        const hasFit = Boolean(normalize(fit?.textContent) || product.querySelector('.nfd-women-fit'));
        product.toggleAttribute('data-nfd-no-fit', !hasFit);
        if (fit) fit.hidden = !hasFit;
        product.querySelectorAll('[data-women-description]').forEach(description => {
          const state = prepareDescription(description);
          const { opening, original, fullLead } = state;
          if (!opening) return;
          const intro = product.querySelector('[data-women-intro-text]');
          const complete = normalize(intro.textContent) === fullLead && !intro.classList.contains('is-clamped');
          // Formatted or linked text stays whole in the tab; plain summaries can be omitted.
          const formatted = original.children.length > 0 || original.hasAttribute('style') || original.hasAttribute('class');
          let html = original.innerHTML;
          let hidden = complete && !formatted;
          if (!complete && !formatted && !desktop.matches && fullLead.startsWith(normalize(intro.textContent))) {
            const copy = original.cloneNode(true);
            const prefix = intro.textContent.trim();
            const full = original.textContent;
            const start = full.indexOf(prefix);
            if (start >= 0) copy.textContent = full.slice(start + prefix.length).trimStart();
            html = copy.innerHTML;
            hidden = !normalize(copy.textContent);
          }
          if (opening.innerHTML !== html) opening.innerHTML = html;
          opening.hidden = original.hidden || Boolean(hidden);
        });
        const tabs = [...product.querySelectorAll('[data-women-tabs] [role="tab"]')];
        const focused = tabs.find(tab => tab === document.activeElement);
        tabs.forEach(tab => {
          const panel = product.querySelector('#' + CSS.escape(tab.getAttribute('aria-controls')));
          tab.hidden = !panel || !meaningful(panel);
          if (tab.hidden) { tab.setAttribute('aria-selected', 'false'); tab.tabIndex = -1; if (panel) panel.hidden = true; }
        });
        const active = tabs.find(tab => !tab.hidden && tab.getAttribute('aria-selected') === 'true') || tabs.find(tab => !tab.hidden);
        if (active) product.activateTab?.(active, Boolean(focused?.hidden));
        const tablist = product.querySelector('[data-women-tabs]');
        if (tablist) tablist.hidden = !active;
        if (specifications && desktop.matches && !specifications.hidden) {
          const information = product.querySelector('.nfd-women-information');
          if (information) {
            information.setAttribute('data-nfd-flow-details', '');
            if (specifications.parentElement !== information) information.prepend(specifications);
            specifications.style.transform = '';
            specifications.classList.add('is-visible');
          }
        }
      };
      const schedule = () => { if (!frame) frame = requestAnimationFrame(sync); };
      const observer = new MutationObserver(schedule);
      observer.observe(product, { childList: true, subtree: true, characterData: true });
      desktop.addEventListener('change', schedule);
      product.addEventListener('nfd:intro-update', schedule);
      window.addEventListener('resize', schedule);
      const onKeydown = event => {
        const tab = event.target.closest('[role="tab"]');
        if (!tab || !event.target.closest('[data-women-tabs]') || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        const tabs = [...product.querySelectorAll('[data-women-tabs] [role="tab"]')].filter(item => !item.hidden);
        if (!tabs.length) return;
        event.preventDefault(); event.stopImmediatePropagation();
        let index = tabs.indexOf(tab);
        if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = tabs.length - 1;
        else index = (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        product.activateTab?.(tabs[index], true);
      };
      product.addEventListener('keydown', onKeydown, true);
      const cleanup = () => {
        observer.disconnect(); desktop.removeEventListener('change', schedule);
        product.removeEventListener('nfd:intro-update', schedule); window.removeEventListener('resize', schedule);
        product.removeEventListener('keydown', onKeydown, true);
        if (frame) cancelAnimationFrame(frame);
        enhanced.delete(product);
      };
      enhanced.set(product, cleanup);
      product.controller?.signal.addEventListener('abort', cleanup, { once: true });
      schedule();
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', initialize);
})();
